import React, { useState, useEffect, useRef } from 'react';
import axios from '../utils/axiosInstance';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { ChevronRight, Plus, Minus, Clock } from 'lucide-react';

const POLL_INTERVAL_MS = 2000;

// Human-friendly labels for the eight sync types. Order mirrors
// sync_state_service.ALL_SYNC_TYPES so dashboard rows have a stable layout.
const SYNC_LABELS = {
  daily_translate: 'Daily translate',
  product_resources: 'Products',
  collection: 'Collections',
  content_resource: 'Articles & Pages',
  menu_link: 'Menus & Links',
  theme: 'Theme strings',
  metafield: 'Metafields',
  metaobject: 'Metaobjects',
  filter_label: 'Filter labels',
};

function formatTimestamp(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatRelative(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (Number.isNaN(diff)) return iso;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatDuration(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const ms = new Date(endIso) - new Date(startIso);
  if (Number.isNaN(ms) || ms < 0) return null;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m < 60) return sec ? `${m}m ${sec}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function StatusBadge({ state }) {
  if (state.stalled) {
    return (
      <Badge
        className="bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900 dark:text-amber-300"
        title="Heartbeat went stale — worker likely died mid-sync."
      >
        Stalled
      </Badge>
    );
  }
  if (state.running) {
    return (
      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300">
        Running
      </Badge>
    );
  }
  if (state.error) {
    return <Badge variant="destructive">Failed</Badge>;
  }
  if (state.last_completed) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900 dark:text-emerald-300">
        Idle
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Never run
    </Badge>
  );
}

function ProgressBar({ progress }) {
  if (!progress || typeof progress !== 'object') return null;
  const { phase, current, total, detail } = progress;
  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs text-muted-foreground mb-1">
        <span>
          {phase || 'Working…'}
          {detail ? ` — ${detail}` : ''}
        </span>
        {typeof current === 'number' && total ? (
          <span>
            {current}/{total}
          </span>
        ) : typeof current === 'number' ? (
          <span>{current.toLocaleString()} walked</span>
        ) : null}
      </div>
      <div className="h-1.5 w-full bg-muted rounded overflow-hidden">
        {total ? (
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${Math.min(100, Math.round((current / total) * 100))}%` }}
          />
        ) : (
          <div className="h-full bg-primary/40 animate-pulse" style={{ width: '40%' }} />
        )}
      </div>
    </div>
  );
}

// One number + label, e.g. "12 added". When the chip represents added/removed
// items AND an items list is available, the chip becomes a button that toggles
// the items list. When the count exists but items don't (data from a sync that
// ran before this feature shipped), the chip stays a static span with a
// tooltip explaining the missing detail.
function CountChip({
  icon: Icon,
  label,
  value,
  tone,
  items,
  open,
  onToggle,
}) {
  if (value == null) return null;
  const toneClasses = {
    added: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
    removed: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
    total: 'bg-muted text-foreground',
    info: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  };
  const expandable = (tone === 'added' || tone === 'removed') && value > 0;
  const hasItems = expandable && Array.isArray(items) && items.length > 0;
  const inner = (
    <>
      {Icon ? <Icon className="h-3 w-3" /> : null}
      <span className="tabular-nums">{value.toLocaleString()}</span>
      <span className="text-muted-foreground font-normal">{label}</span>
      {expandable && hasItems && (
        <ChevronRight
          className={`h-3 w-3 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden
        />
      )}
    </>
  );
  const base = `inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
    toneClasses[tone] || toneClasses.info
  }`;
  if (hasItems) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className={`${base} cursor-pointer hover:brightness-110`}
        title={open ? 'Hide items' : `Show ${value.toLocaleString()} ${label}`}
      >
        {inner}
      </button>
    );
  }
  if (expandable) {
    // Count > 0 but no items array — data predates the item-tracking feature.
    return (
      <span
        className={`${base} opacity-90`}
        title="Item details not recorded for this sync (ran before item tracking was deployed). Re-run the sync to populate."
      >
        {inner}
      </span>
    );
  }
  return <span className={base}>{inner}</span>;
}

// Per-resource-type breakdown for theme sync (e.g. { ONLINE_STORE_THEME: 7519, ... }).
function BreakdownChips({ breakdown }) {
  if (!breakdown || typeof breakdown !== 'object') return null;
  const entries = Object.entries(breakdown).filter(([, n]) => typeof n === 'number' && n > 0);
  if (!entries.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {entries.map(([key, n]) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-muted text-muted-foreground"
        >
          {key.replace(/^ONLINE_STORE_/, '')}: <span className="tabular-nums">{n.toLocaleString()}</span>
        </span>
      ))}
    </div>
  );
}

// Inline items list — renders below the count chips when the user expands
// either Added or Removed. Backend caps items at 50; `count` carries the full
// magnitude so we can show "…and N more" when truncated.
function ItemList({ tone, count, items }) {
  const isAdded = tone === 'added';
  const truncated = items && items.length < count;
  return (
    <ul
      className={`mt-2 ml-1 max-h-64 overflow-y-auto rounded border text-xs font-mono divide-y ${
        isAdded
          ? 'border-emerald-200 dark:border-emerald-900'
          : 'border-red-200 dark:border-red-900'
      }`}
    >
      {(items || []).map((item, i) => (
        <li key={`${item.id || ''}-${i}`} className="px-2 py-1 flex items-center gap-2">
          <span
            className={`text-[10px] ${isAdded ? 'text-emerald-600' : 'text-red-600'}`}
            aria-hidden
          >
            {isAdded ? '+' : '−'}
          </span>
          <span className="truncate" title={item.id || ''}>
            {item.title || <em className="text-muted-foreground">(no title)</em>}
          </span>
        </li>
      ))}
      {truncated && (
        <li className="px-2 py-1 text-muted-foreground italic">
          … and {(count - (items?.length || 0)).toLocaleString()} more (truncated to keep payload small)
        </li>
      )}
    </ul>
  );
}

function SummarySection({ state }) {
  const s = state.summary;
  const [openList, setOpenList] = useState(null); // 'added' | 'removed' | null
  if (!s || typeof s !== 'object') return null;

  // Normalise field names — different sync services use different vocabulary.
  const total =
    s.total_rows ?? s.total_keys ?? s.total_definitions ?? s.total_values ?? null;
  const added =
    s.added_count ?? s.new_definitions ?? s.new_values ?? null;
  const removed =
    s.removed_count ?? s.stale_deleted ?? s.removed_definitions ?? null;
  const imported = s.imported_translations ?? null;
  const duration = formatDuration(s.started_at, s.finished_at);

  const toggle = (which) => setOpenList((cur) => (cur === which ? null : which));

  return (
    <div className="mt-3 pt-3 border-t border-border/60">
      <div className="flex flex-wrap items-center gap-2">
        {total != null && <CountChip label="total" value={total} tone="total" />}
        <CountChip
          icon={Plus}
          label="added"
          value={added}
          tone="added"
          items={s.added}
          open={openList === 'added'}
          onToggle={() => toggle('added')}
        />
        <CountChip
          icon={Minus}
          label="removed"
          value={removed}
          tone="removed"
          items={s.removed}
          open={openList === 'removed'}
          onToggle={() => toggle('removed')}
        />
        {imported != null && imported > 0 && (
          <CountChip label="imported translations" value={imported} tone="info" />
        )}
        {duration && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground ml-auto">
            <Clock className="h-3 w-3" />
            {duration}
          </span>
        )}
      </div>

      <BreakdownChips breakdown={s.by_resource_type} />

      {openList === 'added' && (
        <ItemList tone="added" count={s.added_count} items={s.added} />
      )}
      {openList === 'removed' && (
        <ItemList tone="removed" count={s.removed_count} items={s.removed} />
      )}
    </div>
  );
}

function HistorySection({ syncType, runs }) {
  const [open, setOpen] = useState(false);
  const ours = (runs || []).filter((r) => r.sync_type === syncType);
  // First entry per type from /sync-runs/ endpoint IS the latest run, which is
  // also surfaced as `state.summary` above — skip it here to avoid duplication.
  const older = ours.slice(1);
  if (!older.length) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className={`h-3 w-3 transition-transform ${open ? 'rotate-90' : ''}`} />
          {older.length} earlier run{older.length === 1 ? '' : 's'}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-2 ml-4 text-xs space-y-1">
          {older.map((run) => (
            <li key={run.id} className="flex flex-wrap items-center gap-2 text-muted-foreground">
              <span className="w-32 shrink-0" title={run.finished_at || run.started_at}>
                {formatRelative(run.finished_at || run.started_at)}
              </span>
              <Badge
                className={
                  run.status === 'success'
                    ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900 dark:text-emerald-300'
                    : run.status === 'error'
                    ? 'bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900 dark:text-red-300'
                    : 'bg-muted text-foreground'
                }
              >
                {run.status || 'unknown'}
              </Badge>
              {run.new_count != null && run.new_count > 0 && (
                <span className="text-emerald-600 dark:text-emerald-400 tabular-nums">
                  +{run.new_count}
                </span>
              )}
              {run.deleted_count != null && run.deleted_count > 0 && (
                <span className="text-red-600 dark:text-red-400 tabular-nums">
                  −{run.deleted_count}
                </span>
              )}
              {run.total_count != null && (
                <span className="tabular-nums">{run.total_count.toLocaleString()} total</span>
              )}
              {run.error && (
                <span className="text-red-600 dark:text-red-400 truncate" title={run.error}>
                  {run.error.slice(0, 80)}
                </span>
              )}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function SyncActivity() {
  const [states, setStates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [resetting, setResetting] = useState(null);
  const intervalRef = useRef(null);

  const fetchData = async () => {
    try {
      const [stateRes, runsRes] = await Promise.all([
        axios.get('/api/transly/sync-activity/'),
        axios.get('/api/transly/sync-runs/?limit_per_type=5'),
      ]);
      setStates(Array.isArray(stateRes.data?.states) ? stateRes.data.states : []);
      setRuns(Array.isArray(runsRes.data?.runs) ? runsRes.data.runs : []);
      setLastFetch(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load sync state');
    }
  };

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleReset = async (syncType, isStalled) => {
    const label = SYNC_LABELS[syncType] || syncType;
    const message = isStalled
      ? `Clear the stalled running flag for "${label}"?\n\n`
        + 'Heartbeat has been quiet for 5+ minutes — the worker is almost '
        + 'certainly dead. This does NOT trigger a new sync; it just '
        + 'unblocks the next Sync click.'
      : `Force-clear the running flag for "${label}"?\n\n`
        + 'This sync looks LIVE (heartbeat is recent). Only force-reset if '
        + 'you know the worker is dead — e.g. you just deployed and the '
        + 'previous worker was killed mid-sync. If a worker is genuinely '
        + 'walking pages right now, force-reset will abandon its progress.\n\n'
        + 'Continue?';
    if (!window.confirm(message)) return;
    setResetting(syncType);
    try {
      await axios.post(`/api/transly/sync-activity/${encodeURIComponent(syncType)}/reset/`);
      await fetchData();
    } catch (err) {
      alert('Reset failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setResetting(null);
    }
  };

  const anyRunning = states.some((s) => s.running);

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-foreground">Sync Activity</h1>
        <div className="text-xs text-muted-foreground">
          {anyRunning ? 'Polling every 2s' : 'Idle'}
          {lastFetch && ` · updated ${lastFetch.toLocaleTimeString()}`}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {states.map((state) => (
          <Card key={state.sync_type}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium">
                  {SYNC_LABELS[state.sync_type] || state.sync_type}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <StatusBadge state={state} />
                  {(state.running || state.stalled) && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={resetting === state.sync_type}
                      onClick={() => handleReset(state.sync_type, state.stalled)}
                      title={
                        state.stalled
                          ? 'Clear stalled running flag'
                          : 'Force-clear running flag (use only if you know the worker is dead, e.g. after a deploy)'
                      }
                    >
                      {resetting === state.sync_type
                        ? 'Resetting…'
                        : state.stalled
                        ? 'Reset'
                        : 'Force reset'}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 text-sm">
              {state.running && <ProgressBar progress={state.progress} />}
              {!state.running && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>
                    Last completed:{' '}
                    <span className="text-foreground">
                      {state.last_completed
                        ? `${formatRelative(state.last_completed)} (${formatTimestamp(state.last_completed)})`
                        : '—'}
                    </span>
                  </span>
                </div>
              )}
              {state.error && !state.running && (
                <div className="mt-2 text-xs text-destructive">
                  Error: {state.error}
                </div>
              )}
              {state.last_log && (
                <div
                  className="mt-2 text-xs font-mono text-muted-foreground truncate"
                  title={state.last_log}
                >
                  {state.last_log}
                </div>
              )}

              {/* Structured summary — counts, breakdown chips, added/removed lists. */}
              {!state.running && <SummarySection state={state} />}

              {/* Inline history of older runs from sync_runs audit log. */}
              <HistorySection syncType={state.sync_type} runs={runs} />
            </CardContent>
          </Card>
        ))}
        {!states.length && !error && (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground text-center">
              Loading sync state…
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
